import { getLocalizedPath } from '@/lib/getLocalizedPath';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_SUFFIX_REGEX =
  /^(.+)-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/**
 * Slugify text for URL segment: lowercase, replace spaces/special chars with -,
 * collapse multiple hyphens, trim. Keeps Latin and Arabic letters and numbers.
 */
export function slugify(text: string): string {
  if (!text || typeof text !== 'string') return '';
  return text
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9\u0600-\u06FF-]/g, '') // keep Latin, Arabic, numbers, hyphen
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

export interface ItemForSlug {
  id: string;
  name_en: string;
  name_ar?: string;
}

export interface BlogForSlug {
  id: string;
  name_en: string;
  name_ar?: string;
}

export interface VendorForSlug {
  id: string;
  name_en: string;
  name_ar?: string;
}

export interface CategoryForSlug {
  id: string;
  name_en: string;
  name_ar?: string;
}

export interface SubcategoryForSlug {
  id: string;
  name_en: string;
  name_ar?: string;
}

export function getCategorySlug(category: CategoryForSlug, locale: string): string {
  const name =
    (locale === 'ar' ? category.name_ar || category.name_en : category.name_en) || '';
  const slug = slugify(name);
  return slug ? `${slug}-${category.id}` : category.id;
}

export function getSubcategorySlug(subcategory: SubcategoryForSlug, locale: string): string {
  const name =
    (locale === 'ar' ? subcategory.name_ar || subcategory.name_en : subcategory.name_en) || '';
  const slug = slugify(name);
  return slug ? `${slug}-${subcategory.id}` : subcategory.id;
}

export function getCategoryDetailPath(locale: string, category: CategoryForSlug): string {
  return getLocalizedPath(locale, `/categories/${getCategorySlug(category, locale)}`);
}

export function getSubcategoryDetailPath(
  locale: string,
  category: CategoryForSlug,
  subcategory: SubcategoryForSlug
): string {
  const catSeg = getCategorySlug(category, locale);
  const subSeg = getSubcategorySlug(subcategory, locale);
  return getLocalizedPath(locale, `/categories/${catSeg}/subcategories/${subSeg}`);
}

/** UUID from `/categories/{slug-or-uuid}` segment (same pattern as items). */
export function extractCategoryId(segment: string): string | null {
  return extractItemId(segment);
}

/** UUID from `/subcategories/{slug-or-uuid}` segment. */
export function extractSubcategoryId(segment: string): string | null {
  return extractItemId(segment);
}

export function getItemSlug(
  item: ItemForSlug,
  locale: string
): string {
  const name = (locale === 'ar' ? item.name_ar || item.name_en : item.name_en) || '';
  const slug = slugify(name);
  return slug ? `${slug}-${item.id}` : item.id;
}

export function getBlogSlug(blog: BlogForSlug, locale: string): string {
  const name = (locale === 'ar' ? blog.name_ar || blog.name_en : blog.name_en) || '';
  const slug = slugify(name);
  return slug ? `${slug}-${blog.id}` : blog.id;
}

export function getVendorSlug(vendor: VendorForSlug, locale: string): string {
  const name = (locale === 'ar' ? vendor.name_ar || vendor.name_en : vendor.name_en) || '';
  const slug = slugify(name);
  return slug ? `${slug}-${vendor.id}` : vendor.id;
}

/**
 * Extract item UUID from route segment. Accepts bare UUID or slug-uuid.
 */
export function extractItemId(segment: string): string | null {
  if (!segment || typeof segment !== 'string') return null;
  const s = segment.trim();
  if (UUID_REGEX.test(s)) return s;
  const match = s.match(UUID_SUFFIX_REGEX);
  return match ? match[2] : null;
}

/**
 * Extract blog id from route segment. Accepts bare UUID, slug-uuid, bare numeric id, or legacy slug-numeric-id.
 */
export function extractBlogId(segment: string): string | null {
  if (!segment || typeof segment !== 'string') return null;
  const s = segment.trim();
  if (UUID_REGEX.test(s)) return s;
  const uuidSuffix = s.match(UUID_SUFFIX_REGEX);
  if (uuidSuffix) return uuidSuffix[2];
  if (/^\d+$/.test(s)) return s;
  const legacySlug = s.match(/^(.+)-(\d+)$/);
  return legacySlug ? legacySlug[2] : null;
}

/**
 * Extract vendor UUID from route segment. Accepts bare UUID or slug-uuid (same as item).
 */
export function extractVendorId(segment: string): string | null {
  if (!segment || typeof segment !== 'string') return null;
  const s = segment.trim();
  if (UUID_REGEX.test(s)) return s;
  const match = s.match(UUID_SUFFIX_REGEX);
  return match ? match[2] : null;
}

export function getItemDetailPath(
  locale: string,
  item: ItemForSlug
): string {
  return getLocalizedPath(locale, `/items/${getItemSlug(item, locale)}`);
}

export function getBlogDetailPath(
  locale: string,
  blog: BlogForSlug
): string {
  return getLocalizedPath(locale, `/blogs/${getBlogSlug(blog, locale)}`);
}

export function getVendorDetailPath(
  locale: string,
  vendor: VendorForSlug
): string {
  return getLocalizedPath(locale, `/vendors/${getVendorSlug(vendor, locale)}`);
}
